import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, Globe, Loader2, CheckCircle2, Send } from 'lucide-react';
import Reveal, { EASE } from './Reveal';
import { COMPANY, PRODUCT_OPTIONS } from '../../data/site';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: '',
  company: '',
  email: '',
  phone: '',
  country: '',
  product: '',
  quantity: '',
  message: '',
  website: '',
};

const inputCls =
  'w-full rounded-xl border border-line bg-cream/60 px-4 py-3 text-sm text-ink placeholder:text-ink/35 outline-none transition-all duration-300 focus:border-forest focus:bg-white focus:ring-2 focus:ring-forest/15';

const InfoRow = ({ icon: Icon, label, value, href, testid }) => (
  <a
    href={href}
    data-testid={testid}
    target={href.startsWith('http') ? '_blank' : undefined}
    rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
    className="group flex items-start gap-4 rounded-2xl border border-line bg-white p-5 shadow-card transition-all duration-300 hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-soft"
  >
    <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sage-mist text-forest transition-colors duration-300 group-hover:bg-forest group-hover:text-cream">
      <Icon className="h-5 w-5" />
    </span>
    <span>
      <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-gold">{label}</span>
      <span className="mt-1 block text-sm font-bold text-ink">{value}</span>
    </span>
  </a>
);

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const onPrefill = (e) => {
      if (PRODUCT_OPTIONS.includes(e.detail)) {
        setForm((f) => ({ ...f, product: e.detail }));
      }
    };
    window.addEventListener('prefill-product', onPrefill);
    return () => window.removeEventListener('prefill-product', onPrefill);
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/enquiries`, form);
      if (res.data?.status === 'success') {
        setSent(true);
        toast.success('Enquiry sent successfully.');
      } else {
        throw new Error('Unexpected response');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Something went wrong. Please try again or email us directly.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-cream-deep py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold">Enquiry / Contact</p>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-4 max-w-3xl font-display text-4xl font-medium leading-[1.1] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            Let&apos;s talk <span className="accent-italic font-semibold">business.</span>
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="space-y-4 lg:col-span-5">
            <Reveal>
              <div>
                <h3 className="font-display text-2xl font-semibold text-ink">{COMPANY.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  Share your requirement and our team will respond with product, packing and documentation details.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <InfoRow
                icon={MapPin}
                label="Location"
                value={COMPANY.location}
                href="https://maps.google.com/?q=Pune,+Maharashtra,+India+412105"
                testid="contact-location-link"
              />
            </Reveal>
            <Reveal delay={0.1}>
              <InfoRow icon={Phone} label="Phone / WhatsApp" value={COMPANY.phone} href={`tel:${COMPANY.phoneRaw}`} testid="contact-phone-link" />
            </Reveal>
            <Reveal delay={0.15}>
              <InfoRow icon={Mail} label="Email" value={COMPANY.email} href={`mailto:${COMPANY.email}`} testid="contact-email-link" />
            </Reveal>
            <Reveal delay={0.2}>
              <InfoRow icon={Globe} label="Website" value={COMPANY.website} href={`https://${COMPANY.website}`} testid="contact-website-link" />
            </Reveal>
            <Reveal delay={0.25}>
              <div className="flex flex-wrap gap-3 pt-2">
                <a
                  href={`mailto:${COMPANY.email}`}
                  data-testid="contact-email-us-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-forest px-6 py-3 text-sm font-bold text-cream shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:bg-forest-deep"
                >
                  <Mail className="h-4 w-4" /> Email Us
                </a>
                <a
                  href={COMPANY.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-forest/25 bg-white px-6 py-3 text-sm font-bold text-forest transition-all duration-300 hover:-translate-y-0.5 hover:border-forest"
                >
                  WhatsApp
                </a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="rounded-[2rem] border border-line bg-white p-6 shadow-soft sm:p-9">
              <AnimatePresence mode="wait">
                {sent ? (
                  <motion.div
                    key="success"
                    data-testid="enquiry-success-message"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: EASE }}
                    className="flex min-h-[26rem] flex-col items-center justify-center text-center"
                  >
                    <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-sage-mist">
                      <CheckCircle2 className="h-8 w-8 text-forest" />
                    </span>
                    <h3 className="mt-6 font-display text-2xl font-semibold text-ink">Thank you.</h3>
                    <p className="mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground">
                      Your enquiry has been received. Our team will get back to you shortly.
                    </p>
                    <button
                      data-testid="enquiry-send-another-btn"
                      onClick={() => {
                        setSent(false);
                        setForm(EMPTY);
                      }}
                      className="mt-7 rounded-full border border-forest/25 px-6 py-3 text-sm font-bold text-forest transition-colors hover:border-forest"
                    >
                      Send Another Enquiry
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    data-testid="enquiry-form"
                    onSubmit={submit}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, y: -14 }}
                    className="grid grid-cols-1 gap-4 sm:grid-cols-2"
                  >
                    <div className="absolute -left-[9999px] -top-[9999px]" aria-hidden="true">
                      <label>
                        Website
                        <input
                          type="text"
                          name="website"
                          data-testid="enquiry-honeypot-input"
                          tabIndex={-1}
                          autoComplete="off"
                          value={form.website}
                          onChange={set('website')}
                        />
                      </label>
                    </div>

                    <div>
                      <label htmlFor="enq-name" className="mb-1.5 block text-xs font-bold text-ink">Full Name *</label>
                      <input id="enq-name" data-testid="enquiry-name-input" required minLength={2} maxLength={120} value={form.name} onChange={set('name')} placeholder="Your full name" className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="enq-company" className="mb-1.5 block text-xs font-bold text-ink">Company Name</label>
                      <input id="enq-company" data-testid="enquiry-company-input" maxLength={160} value={form.company} onChange={set('company')} placeholder="Your company" className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="enq-email" className="mb-1.5 block text-xs font-bold text-ink">Business Email *</label>
                      <input id="enq-email" type="email" data-testid="enquiry-email-input" required value={form.email} onChange={set('email')} placeholder="name@company.com" className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="enq-phone" className="mb-1.5 block text-xs font-bold text-ink">Phone / WhatsApp</label>
                      <input id="enq-phone" type="tel" data-testid="enquiry-phone-input" maxLength={40} value={form.phone} onChange={set('phone')} placeholder="+971 ..." className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="enq-country" className="mb-1.5 block text-xs font-bold text-ink">Country</label>
                      <input id="enq-country" data-testid="enquiry-country-input" maxLength={80} value={form.country} onChange={set('country')} placeholder="United Arab Emirates" className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="enq-product" className="mb-1.5 block text-xs font-bold text-ink">Product Interested In *</label>
                      <select id="enq-product" data-testid="enquiry-product-select" required value={form.product} onChange={set('product')} className={inputCls}>
                        <option value="" disabled>Select a product</option>
                        {PRODUCT_OPTIONS.map((p) => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="enq-quantity" className="mb-1.5 block text-xs font-bold text-ink">Quantity / Requirement</label>
                      <input id="enq-quantity" data-testid="enquiry-quantity-input" maxLength={160} value={form.quantity} onChange={set('quantity')} placeholder="e.g. trial order, one container, monthly requirement" className={inputCls} />
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="enq-message" className="mb-1.5 block text-xs font-bold text-ink">Message</label>
                      <textarea id="enq-message" data-testid="enquiry-message-input" rows={4} maxLength={3000} value={form.message} onChange={set('message')} placeholder="Tell us about your requirement, destination port, packing preference…" className={`${inputCls} resize-y`} />
                    </div>

                    {error && (
                      <p data-testid="enquiry-error-message" className="text-sm font-semibold text-destructive sm:col-span-2">
                        {error}
                      </p>
                    )}

                    <div className="sm:col-span-2">
                      <button
                        type="submit"
                        data-testid="enquiry-form-submit"
                        disabled={loading}
                        className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-gold px-8 py-4 text-sm font-bold text-ink shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-soft hover:shadow-lift disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                          </>
                        ) : (
                          <>
                            Send Enquiry
                            <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                          </>
                        )}
                      </button>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-14 overflow-hidden rounded-[2rem] border border-line shadow-card">
            <iframe
              title="SKycodeX AgroTech location — Pune, Maharashtra, India 412105"
              data-testid="contact-map"
              src="https://maps.google.com/maps?q=Pune,%20Maharashtra,%20India%20412105&t=&z=11&ie=UTF8&iwloc=&output=embed"
              className="h-80 w-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
